import { useMemo, useState, useEffect, useRef } from "react";
import {
  Checkbox,
  ComposedModal,
  IconButton,
  ModalBody,
  ModalHeader,
  SkeletonText,
  Tag,
} from "@carbon/react";
import { CheckmarkFilled, Email } from "@carbon/icons-react";
import { Pagination } from "../../../components";
import i18next from "../../../i18n";
import InviteSupplier from "../../../components/Material/InviteSupplier/InviteSupplier";
import { BuyerSellerResponse } from "../../../types";
import { usePotentialSuppliers } from "../../../hooks/useSuppliers";
import { useUser } from "../../../context";
import { getCompanyLegalAddressLabel } from "../../../utils/address";
import {
  deriveJointSelectedSuppliers,
  getUniqueJointSuppliers,
} from "../supplierRows";

type JointSuppliersCheckboxListProps = {
  suppliers: any[];
  ordersToSubmit: any[];
  orderInvitation: any[];
  handleOrdersToSubmit: (orders: any, isJoint?: string) => void;
};

const JointSuppliersCheckboxList = ({
  suppliers,
  ordersToSubmit,
  orderInvitation,
  handleOrdersToSubmit,
}: JointSuppliersCheckboxListProps) => {
  const [open, setOpen] = useState(false);
  const [materialId, setMaterialId] = useState("");
  const [materialName, setMaterialName] = useState("");
  const [potentialSuppliersPage, setPotentialSuppliersPage] = useState(0);
  const [hasFetchedPotentialSuppliers, setHasFetchedPotentialSuppliers] =
    useState(false);
  const { user } = useUser();

  const {
    mutate: getPotentialSuppliers,
    data: potentialSuppliers,
    isPending: isLoadingPotentialSuppliers,
  } = usePotentialSuppliers();

  const allMaterialIds = useMemo(() => {
    return ordersToSubmit.map((order: any) => order.id).filter(Boolean);
  }, [ordersToSubmit]);

  const prevPotentialSuppliersPage = useRef(potentialSuppliersPage);

  useEffect(() => {
    const pageChanged =
      prevPotentialSuppliersPage.current !== potentialSuppliersPage;
    prevPotentialSuppliersPage.current = potentialSuppliersPage;

    if (
      (!hasFetchedPotentialSuppliers || pageChanged) &&
      allMaterialIds.length > 0 &&
      user?.company?.country?.id
    ) {
      getPotentialSuppliers({
        countryId: user.company.country.id,
        page: potentialSuppliersPage,
        materialIds: allMaterialIds,
      });
      if (!hasFetchedPotentialSuppliers) {
        setHasFetchedPotentialSuppliers(true);
      }
    }
  }, [
    allMaterialIds,
    user?.company?.country?.id,
    hasFetchedPotentialSuppliers,
    potentialSuppliersPage,
    getPotentialSuppliers,
  ]);

  const handleClick = (id: string, name: string) => {
    setOpen(true);
    setMaterialId(id);
    setMaterialName(name);
  };

  const onCloseInvitation = () => setOpen(false);

  const suppliersObject = useMemo(
    () => Object.keys(suppliers).map((key: any) => suppliers[key]),
    [suppliers]
  ) as [BuyerSellerResponse[]];

  const baseSuppliersList = useMemo(
    () => getUniqueJointSuppliers(suppliersObject),
    [suppliersObject]
  );

  const suppliersToShowList = useMemo(() => {
    return baseSuppliersList;
  }, [baseSuppliersList]);

  const allSuppliers = ordersToSubmit.flatMap(
    (order) => order?.suppliers || []
  );

  const selectedSuppliers = deriveJointSelectedSuppliers(ordersToSubmit);

  const selectedSupplierIds = selectedSuppliers
    .filter((s) => s.type === "existing")
    .map((s) => s.id);

  const availableSuppliers = suppliersToShowList.filter(
    (supplier: any) => !selectedSupplierIds.includes(supplier.id)
  );

  const handlePotentialSupplierSelection = (supplier: any) => {
    const supplierData = {
      materialId: "ALL", // Joint mode always uses "ALL"
      supplierId: supplier.user.id,
      supplierInfo: {
        id: supplier.user.id,
        email: supplier.user.email,
        name: `${supplier.user.firstName || ""} ${supplier.user.lastName || ""}`.trim(),
        companyName: supplier.user.company?.name || "",
      },
    };

    const isSelected = allSuppliers.some(
      (sup: any) => sup.id === supplier.user.id
    );

    handleOrdersToSubmit(
      supplierData,
      isSelected ? "removeExistingSupplier" : "existingSupplier"
    );
  };

  const handleRemoveSelectedSupplier = (supplier: any) => {
    if (supplier.type === "existing") {
      const supplierData = {
        materialId: "ALL",
        supplierId: supplier.id,
        supplierInfo: {
          id: supplier.id,
          email: supplier.email,
          name: supplier.name,
          companyName: supplier.name,
          validated: supplier.validated,
        },
      };
      handleOrdersToSubmit(supplierData, "removeExistingSupplier");
    } else if (supplier.type === "email") {
      const parentEmailsList =
        supplier.supplierData?.emailsList ?? supplier.email;
      const allEmails = String(parentEmailsList)
        .split(":")
        .map((e: string) => e.trim())
        .filter(Boolean);

      if (allEmails.length <= 1) {
        handleOrdersToSubmit(
          { materialId: "ALL", emailsList: parentEmailsList },
          "isJoint"
        );
      } else {
        handleOrdersToSubmit(
          { materialId: "ALL", emailsList: parentEmailsList },
          "isJoint"
        );
        allEmails
          .filter((email: string) => email !== supplier.email)
          .forEach((email: string) => {
            handleOrdersToSubmit(
              { materialId: "ALL", emailsList: email },
              "isJoint"
            );
          });
      }
    }
  };

  const handleSupplierSelectionWithFullData = (supplier: any) => {
    const supplierData = {
      materialId: "ALL",
      supplierId: supplier.id,
      supplierInfo: {
        id: supplier.id,
        email: supplier.email,
        name: `${supplier.firstName || ""} ${supplier.lastName || ""}`.trim(),
        companyName: supplier.company?.name || "",
        validated: supplier.validated,
      },
    };

    const isSelected = allSuppliers.some((sup: any) => sup.id === supplier.id);

    handleOrdersToSubmit(
      supplierData,
      isSelected ? "removeExistingSupplier" : "existingSupplier"
    );
  };

  return (
    <div className="order-joint-suppliers">
      <div className="order-joint-suppliers__invite">
        <IconButton
          kind="ghost"
          size="sm"
          label={i18next.t("invite.invite")}
          align="left"
          onClick={() => handleClick("", "")}
        >
          <Email />
        </IconButton>
      </div>

      <fieldset className="order-supplier-section">
        {selectedSuppliers.length > 0 && (
          <div className="order-supplier-group">
            {selectedSuppliers.map((supplier) => {
              const fullSupplier = suppliersToShowList.find(
                (s: any) => s.id === supplier.id
              );
              const potentialMatch = potentialSuppliers?.content?.find(
                (p: any) => p.user?.id === supplier.id
              );
              const company =
                fullSupplier?.company || potentialMatch?.user?.company;
              const address = getCompanyLegalAddressLabel(company);
              return (
                <Checkbox
                  key={`selected-joint-${supplier.id}`}
                  id={`selected-joint-${supplier.id}`}
                  checked={true}
                  onChange={() => handleRemoveSelectedSupplier(supplier)}
                  labelText={
                    <span className="order-supplier-card__name">
                      {supplier.name}
                      {supplier.validated && (
                        <CheckmarkFilled className="order-supplier-card__badge" />
                      )}
                      {supplier.type === "email" && (
                        <Tag type="warm-gray" size="sm">
                          {i18next.t("offer.invited")}
                        </Tag>
                      )}
                      {address && (
                        <span className="order-supplier-card__addr">
                          {address}
                        </span>
                      )}
                    </span>
                  }
                />
              );
            })}
          </div>
        )}

        {availableSuppliers.length > 0 && (
          <div className="order-supplier-group">
            {availableSuppliers.map((supplier) => {
              const isChecked = allSuppliers?.find(
                (sup) => sup?.id === supplier?.id
              );
              const address = getCompanyLegalAddressLabel(supplier.company);
              return (
                <Checkbox
                  key={supplier.id}
                  id={`joint-available-${supplier.id}`}
                  checked={!!isChecked}
                  onChange={() => handleSupplierSelectionWithFullData(supplier)}
                  labelText={
                    <span className="order-supplier-card__name">
                      {supplier.company?.name ||
                        `${supplier.firstName || ""} ${supplier.lastName || ""}`.trim()}
                      {supplier.validated && (
                        <CheckmarkFilled className="order-supplier-card__badge" />
                      )}
                      {address && (
                        <span className="order-supplier-card__addr">
                          {address}
                        </span>
                      )}
                    </span>
                  }
                />
              );
            })}
          </div>
        )}

        {(isLoadingPotentialSuppliers ||
          (potentialSuppliers?.content &&
            potentialSuppliers.content.length > 0)) && (
          <div className="order-supplier-group">
            <div className="order-supplier-subhead">
              {i18next.t("material.potencialSuppliers") || "Potential Suppliers"}
              {!isLoadingPotentialSuppliers &&
                !!potentialSuppliers?.totalElements && (
                  <Tag type="blue" size="sm">
                    {potentialSuppliers.totalElements}
                  </Tag>
                )}
            </div>

            {isLoadingPotentialSuppliers ? (
              <SkeletonText paragraph lineCount={3} />
            ) : (
              potentialSuppliers?.content
                ?.filter((supplier: any) => {
                  const isAlreadySelected = selectedSupplierIds.includes(
                    supplier.user.id
                  );
                  const isInMainSuppliers = suppliersToShowList.some(
                    (mainSupplier: any) => mainSupplier.id === supplier.user.id
                  );
                  return !isAlreadySelected && !isInMainSuppliers;
                })
                .map((supplier: any) => {
                  const address = getCompanyLegalAddressLabel(
                    supplier.user.company
                  );
                  return (
                    <Checkbox
                      key={`joint-potential-${supplier.user.id}`}
                      id={`joint-potential-${supplier.user.id}`}
                      checked={false}
                      onChange={() =>
                        handlePotentialSupplierSelection(supplier)
                      }
                      labelText={
                        <span className="order-supplier-card__name">
                          {supplier.user.company?.name ||
                            `${supplier.user.firstName || ""} ${supplier.user.lastName || ""}`.trim()}
                          {address && (
                            <span className="order-supplier-card__addr">
                              {address}
                            </span>
                          )}
                        </span>
                      }
                    />
                  );
                })
            )}

            {potentialSuppliers && (
              <Pagination
                results={potentialSuppliers.numberOfElements}
                total={potentialSuppliers.totalElements}
                amountOfPages={potentialSuppliers.totalPages}
                currentPage={potentialSuppliersPage}
                setPageNumber={setPotentialSuppliersPage}
              />
            )}
          </div>
        )}

        <ComposedModal open={open} onClose={() => setOpen(false)} size="sm">
          <ModalHeader title={i18next.t("orderByRequest.inviteSupplier")} />
          <ModalBody hasForm>
            <InviteSupplier
              isOrder
              isJoint
              materialId={materialId}
              materialName={materialName}
              onCloseInvitation={onCloseInvitation}
              orderInvitation={orderInvitation}
              handleOrdersToSubmit={handleOrdersToSubmit}
            />
          </ModalBody>
        </ComposedModal>
      </fieldset>
    </div>
  );
};

export default JointSuppliersCheckboxList;
